import {
  createListenerMiddleware,
  isAnyOf,
  type ThunkDispatch
} from '@reduxjs/toolkit';
import type { AnyAction } from 'redux';
import { PresenceChannel } from 'pusher-js';
import { OVERVIEW_CLASSES_REFRESH_THROTTLE_MS } from 'constants/class-management.constant';
import {
  CLASS_PARTICIPANT_STATUS,
  CLASS_SESSION_EVENT,
  CLASS_SESSION_STATUS
} from 'enums';
import {
  ClassAllowlistChangedEvent,
  ClassFocusSession,
  ClassInfo,
  ClassParticipantUpdatedEvent,
  ClassSessionEndedEvent,
  ClassSessionParticipant,
  ClassTasksUpdatedEvent,
  ClassUnlockRequest,
  ClassUnlockRequestedEvent
} from 'interfaces/classes.interface';
import {
  subscribeToPresenceClassChannel,
  unsubscribeFromPresenceClassChannel
} from 'services/pusher-client';
import {
  fetchLiveParticipants,
  fetchTeacherClasses
} from 'store/reducer/class-management/extra';
import {
  applyAllowlistChanged,
  applyParticipantUpdated,
  applySessionEnded,
  applyTasksUpdated,
  applyUnlockRequested,
  updateClasses,
  updateLiveSession,
  updateSelectedClass
} from 'store/reducer/class-management/slice';

type ClassPresenceListenerState = {
  classManagement: {
    classes: ClassInfo[];
    selectedClass: ClassInfo | null;
    liveSession: ClassFocusSession | null;
    participants: ClassSessionParticipant[];
  };
};

type ClassPresenceDispatch = ThunkDispatch<
  ClassPresenceListenerState,
  unknown,
  AnyAction
>;

type ClassPresenceHandlers = {
  onParticipantUpdated: (payload: ClassParticipantUpdatedEvent) => void;
  onAllowlistChanged: (payload: ClassAllowlistChangedEvent) => void;
  onTasksUpdated: (payload: ClassTasksUpdatedEvent) => void;
  onUnlockRequested: (
    payload: ClassUnlockRequestedEvent | ClassUnlockRequest
  ) => void;
  onSessionEnded: (payload: ClassSessionEndedEvent) => void;
};

type ActiveClassPresence = {
  teamId: string;
  channel: PresenceChannel;
  handlers: ClassPresenceHandlers;
};

const activeClassPresences = new Map<string, ActiveClassPresence>();
let overviewClassesRefreshTimerId: number | null = null;

const scheduleOverviewClassesRefresh = (dispatch: ClassPresenceDispatch) => {
  if (overviewClassesRefreshTimerId !== null) {
    return;
  }
  overviewClassesRefreshTimerId = window.setTimeout(() => {
    overviewClassesRefreshTimerId = null;
    dispatch(fetchTeacherClasses());
  }, OVERVIEW_CLASSES_REFRESH_THROTTLE_MS);
};

const isJoinLeaveStatus = (status: CLASS_PARTICIPANT_STATUS): boolean =>
  status === CLASS_PARTICIPANT_STATUS.JOINED ||
  status === CLASS_PARTICIPANT_STATUS.LEFT;

const createParticipantUpdatedHandler = (
  dispatch: ClassPresenceDispatch,
  getState: () => ClassPresenceListenerState
) => {
  return (payload: ClassParticipantUpdatedEvent) => {
    if (!payload?.class_session_id) return;

    const { liveSession, participants } = getState().classManagement;
    const matchesOpenLiveSession = liveSession?.id === payload.class_session_id;
    const isKnownStudent = participants.some(
      (participant) => participant.student_id === payload.student_id
    );

    if (matchesOpenLiveSession) {
      dispatch(applyParticipantUpdated(payload));
      if (!isKnownStudent) {
        dispatch(fetchLiveParticipants(payload.class_session_id));
      }
      return;
    }

    // Overview (or another class): presence can update chips without a loaded roster.
    if (isJoinLeaveStatus(payload.join_status)) {
      scheduleOverviewClassesRefresh(dispatch);
    }
  };
};

const createUnlockRequestedHandler = (dispatch: ClassPresenceDispatch) => {
  return (payload: ClassUnlockRequestedEvent | ClassUnlockRequest) => {
    const request =
      (payload as ClassUnlockRequestedEvent)?.unlock_request ??
      (payload as ClassUnlockRequest);
    request?.id && dispatch(applyUnlockRequested(request));
  };
};

const createSessionEndedHandler = (
  dispatch: ClassPresenceDispatch,
  _teamId: string
) => {
  return (payload: ClassSessionEndedEvent) => {
    if (!payload?.class_session_id) return;
    dispatch(
      applySessionEnded({
        class_session_id: payload.class_session_id,
        ended_at: payload.ended_at ?? new Date().toISOString()
      })
    );
    // Sessions list is owned by the history modal; overview chips refresh only.
    scheduleOverviewClassesRefresh(dispatch);
  };
};

const createClassPresenceHandlers = (
  dispatch: ClassPresenceDispatch,
  getState: () => ClassPresenceListenerState,
  teamId: string
): ClassPresenceHandlers => ({
  onParticipantUpdated: createParticipantUpdatedHandler(dispatch, getState),
  onAllowlistChanged: (payload) => {
    payload?.class_session_id && dispatch(applyAllowlistChanged(payload));
  },
  onTasksUpdated: (payload) => {
    payload?.class_session_id && dispatch(applyTasksUpdated(payload));
  },
  onUnlockRequested: createUnlockRequestedHandler(dispatch),
  onSessionEnded: createSessionEndedHandler(dispatch, teamId)
});

const bindClassPresenceChannel = (
  teamId: string,
  handlers: ClassPresenceHandlers
): PresenceChannel | null => {
  const channel = subscribeToPresenceClassChannel(teamId);
  if (!channel) return null;

  channel.bind(
    CLASS_SESSION_EVENT.PARTICIPANT_UPDATED,
    handlers.onParticipantUpdated
  );
  channel.bind(
    CLASS_SESSION_EVENT.ALLOWLIST_CHANGED,
    handlers.onAllowlistChanged
  );
  channel.bind(CLASS_SESSION_EVENT.TASKS_UPDATED, handlers.onTasksUpdated);
  channel.bind(
    CLASS_SESSION_EVENT.UNLOCK_REQUESTED,
    handlers.onUnlockRequested
  );
  channel.bind(CLASS_SESSION_EVENT.SESSION_ENDED, handlers.onSessionEnded);

  return channel;
};

const unbindClassPresenceChannel = (
  channel: PresenceChannel,
  handlers: ClassPresenceHandlers
) => {
  channel.unbind(
    CLASS_SESSION_EVENT.PARTICIPANT_UPDATED,
    handlers.onParticipantUpdated
  );
  channel.unbind(
    CLASS_SESSION_EVENT.ALLOWLIST_CHANGED,
    handlers.onAllowlistChanged
  );
  channel.unbind(CLASS_SESSION_EVENT.TASKS_UPDATED, handlers.onTasksUpdated);
  channel.unbind(
    CLASS_SESSION_EVENT.UNLOCK_REQUESTED,
    handlers.onUnlockRequested
  );
  channel.unbind(CLASS_SESSION_EVENT.SESSION_ENDED, handlers.onSessionEnded);
};

const teardownClassPresence = (teamId: string) => {
  const active = activeClassPresences.get(teamId);
  if (!active) return;
  unbindClassPresenceChannel(active.channel, active.handlers);
  unsubscribeFromPresenceClassChannel(teamId);
  activeClassPresences.delete(teamId);
};

const teardownAllClassPresence = () => {
  [...activeClassPresences.keys()].forEach(teardownClassPresence);
};

const getLiveClassTeamIds = (state: ClassPresenceListenerState): string[] => {
  const teamIds = new Set<string>();
  state.classManagement.classes.forEach((classInfo) => {
    if (classInfo.live_session?.status === CLASS_SESSION_STATUS.LIVE) {
      teamIds.add(classInfo.id);
    }
  });
  const { selectedClass, liveSession } = state.classManagement;
  if (
    selectedClass?.id &&
    liveSession?.status === CLASS_SESSION_STATUS.LIVE &&
    liveSession.team_id === selectedClass.id
  ) {
    teamIds.add(selectedClass.id);
  }
  return [...teamIds];
};

const syncClassPresenceSubscription = (
  dispatch: ClassPresenceDispatch,
  getState: () => ClassPresenceListenerState
) => {
  const desiredTeamIds = new Set(getLiveClassTeamIds(getState()));

  [...activeClassPresences.keys()].forEach((teamId) => {
    if (!desiredTeamIds.has(teamId)) {
      teardownClassPresence(teamId);
    }
  });

  desiredTeamIds.forEach((teamId) => {
    if (activeClassPresences.has(teamId)) {
      return;
    }
    const handlers = createClassPresenceHandlers(dispatch, getState, teamId);
    const channel = bindClassPresenceChannel(teamId, handlers);
    if (!channel) {
      return;
    }
    activeClassPresences.set(teamId, { teamId, channel, handlers });
  });

  if (desiredTeamIds.size === 0) {
    teardownAllClassPresence();
  }
};

const classPresenceListener = createListenerMiddleware();

classPresenceListener.startListening({
  matcher: isAnyOf(
    updateLiveSession,
    applySessionEnded,
    updateSelectedClass,
    updateClasses
  ),
  effect: (_action, listenerApi) => {
    syncClassPresenceSubscription(
      listenerApi.dispatch as ClassPresenceDispatch,
      () => listenerApi.getState() as ClassPresenceListenerState
    );
  }
});

export const classPresenceListenerMiddleware = classPresenceListener.middleware;
